import { forwardRef, useEffect, useState } from "react";
import { findKey } from "lodash";
import {ConclusionKeys, EndStatusEnum} from "../utils/surveyStatusSchemas";
import {Text, Button, Flex, Divider} from "@chakra-ui/react";
import QuestionField from "./QuestionField";
import { FindingAreaSchemaType, QuestionSchemaType, ResponseSchemaType } from "../utils/surveyStatusSchemas";
import { mdiChevronTripleRight } from "@mdi/js";
import Icon from "@mdi/react";
import { nextQuestionIdOrConclusion } from "../utils/SurveyExtensions";

interface SurveySectionProps {
    findingArea: FindingAreaSchemaType;
    completionHandler: (index: number, sectionStatus: EndStatusEnum, findingAreaId: string, lastAnswerId: string, branchingPath: string[], conclusionResult: string | undefined) => void;
    surveyIndex: number;
    initialStack: Array<ResponseSchemaType>;
    isSkipped: boolean;
}

const SurveySection = forwardRef<HTMLDivElement, SurveySectionProps>(({
    findingArea: { id, name, questions },
    completionHandler,
    surveyIndex,
    initialStack,
    isSkipped
}, ref) => {
    const [hideSection, setHideSection] = useState(isSkipped);
    const [lastAnswerId, setLastAnswerId] = useState("");
    const [selectedStack, setSelectedStack] = useState<Array<ResponseSchemaType>>(initialStack);
    const [endStatus, setEndStatus] = useState<EndStatusEnum>(EndStatusEnum.NOT_STARTED);
    const [conclusionResult, setConclusionResult] = useState<string>();

    const findNextQuestion = (id: string): QuestionSchemaType | undefined => {
        const nextQuestion = questions.find(q => q.id === id);
        if (!nextQuestion) {
            console.warn("No question found for ID: ${id}");
            return undefined;
        }
        return nextQuestion;
    };

    function addToSelectedStack(question: QuestionSchemaType, response: string, lastAnswerId: string) {
        setLastAnswerId(lastAnswerId);
        const index = selectedStack.findIndex(i => i.question.id === question.id);
        const outputArray = selectedStack.slice(0, index + 1);
        const next_id = nextQuestionIdOrConclusion(question, response);
        let endStatus = EndStatusEnum.IN_PROGRESS;

        if (!Object.values(ConclusionKeys).includes(next_id as ConclusionKeys)) {
            const nextQuestion = findNextQuestion(next_id);
            if (nextQuestion) {
                outputArray.push({
                    question: nextQuestion!,
                    response: "",
                });
            }
        } else {
            endStatus = EndStatusEnum.COMPLETED;
            const conclusionResult = findKey(ConclusionKeys, o => o === next_id);
            setConclusionResult(conclusionResult);
        }
        setSelectedStack(outputArray);
        setEndStatus(endStatus);
        return;
    }

    function handleOnHide() {
        setHideSection(!hideSection);
        const status = !hideSection ? EndStatusEnum.SKIPPED : EndStatusEnum.IN_PROGRESS;
        setEndStatus(status);
        setSelectedStack([
            {
                question: questions[0],
                response: "",
            },
        ]);
    }
    
    function getBranchingPath(): string[] {
        if (selectedStack == undefined || selectedStack == null) return new Array<string>();
       
        const branchingPath: string[] = selectedStack.map(stackObject => stackObject.question.id);
        return (branchingPath ?? new Array<string>());
    }

    useEffect(() => {
        if (endStatus !== EndStatusEnum.NOT_STARTED) {
            completionHandler(surveyIndex, endStatus, id, lastAnswerId, getBranchingPath(), conclusionResult);
        }
    }, [endStatus, surveyIndex, completionHandler, conclusionResult, id, lastAnswerId]);

    return (
        <div ref={ref}>
            <Flex justify="space-between" wrap="wrap" maxW="100%" marginBottom={8}>
                <Text variant="sectionHeader">{name}</Text>
                <Button
                    rightIcon={!hideSection ? <Icon size="2rem" path={mdiChevronTripleRight} /> : <></>}
                    variant="solid"
                    colorScheme={!hideSection ? "gray" : "blue"}
                    onClick={() => {
                        handleOnHide();
                    }}
                >
                    {!hideSection ? "Skip" : "Fill-Out"}
                </Button>
            </Flex>
            {!hideSection && (
                <Flex display="flex" flexFlow="column nowrap" gap="2rem">
                    {selectedStack.map((item) => (
                        <QuestionField 
                            key={item.question.id}
                            question={item.question}
                            responseCallback={addToSelectedStack}
                            defaultResponse={item.response}
                            findingAreaId={id}
                        />
                    ))}
                </Flex>
            )}
            <Divider p="1rem 0 1rem 0" />
        </div>
    );
});

export default SurveySection;
